/**
 * Período de ENTRADA NA ETAPA — filtro do quadro do CRM.
 *
 * A régua é `Lead.entrou_etapa_em` (quando o lead caiu na coluna em que
 * está), não `created_at` (quando entrou no CRM). São coisas diferentes: um
 * lead de março que foi para Negociação hoje "entrou nesta coluna hoje".
 *
 * Os limites são de CALENDÁRIO e no fuso do usuário — "esta semana" é a
 * semana corrente (começa na segunda), não "últimos 7 dias". É assim que a
 * pergunta é feita na prática ("o que entrou esta semana?").
 *
 * O intervalo sai como data local `YYYY-MM-DD` (o backend interpreta no fuso
 * do projeto). Montar com `toISOString()` colocaria o dia ANTERIOR para quem
 * está em São Paulo — o erro que a diretriz de datas do projeto proíbe.
 */

export const PERIODOS_ENTRADA = [
  { id: "", label: "Qualquer data", curto: "" },
  { id: "hoje", label: "Hoje", curto: "hoje" },
  { id: "semana", label: "Esta semana", curto: "esta semana" },
  { id: "mes", label: "Este mês", curto: "este mês" },
  { id: "ano", label: "Este ano", curto: "este ano" },
];

/** Datas escolhidas a dedo — não é um dos presets acima. */
export const PERIODO_PERSONALIZADO = "personalizado";

/** `Date` -> `YYYY-MM-DD` no fuso LOCAL (sem o off-by-one do toISOString). */
export const isoLocal = (data) => {
  const mes = String(data.getMonth() + 1).padStart(2, "0");
  const dia = String(data.getDate()).padStart(2, "0");
  return `${data.getFullYear()}-${mes}-${dia}`;
};

/**
 * Resolve o preset num intervalo inclusivo `{de, ate}` (ambos YYYY-MM-DD).
 * Preset vazio/desconhecido devolve `{de:"", ate:""}` (sem filtro).
 * `base` existe para o teste fixar "hoje".
 */
export function intervaloDoPeriodo(preset, base = new Date()) {
  const vazio = { de: "", ate: "" };
  if (!preset) return vazio;

  const hoje = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  let inicio;
  if (preset === "hoje") {
    inicio = hoje;
  } else if (preset === "semana") {
    // getDay(): 0=domingo. A semana comercial começa na segunda.
    const diasDesdeSegunda = (hoje.getDay() + 6) % 7;
    inicio = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - diasDesdeSegunda);
  } else if (preset === "mes") {
    inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  } else if (preset === "ano") {
    inicio = new Date(hoje.getFullYear(), 0, 1);
  } else {
    return vazio;
  }
  // `ate` é hoje: entrada na etapa não acontece no futuro, e deixar aberto
  // evitaria ter que mexer no fim do intervalo à meia-noite.
  return { de: isoLocal(inicio), ate: isoLocal(hoje) };
}

/** 'YYYY-MM-DD' -> 'DD/MM/AAAA' (vazio vira vazio). */
export const dataBr = (iso) => (iso ? iso.split("-").reverse().join("/") : "");

/**
 * Rótulo de um intervalo QUALQUER, incluindo os abertos de um lado só —
 * "a partir de X" / "até Y" é informação útil e o backend aceita os dois.
 */
export function rotuloIntervaloLivre(de, ate) {
  if (de && ate) return de === ate ? dataBr(de) : `${dataBr(de)} a ${dataBr(ate)}`;
  if (de) return `a partir de ${dataBr(de)}`;
  if (ate) return `até ${dataBr(ate)}`;
  return "";
}

/** Rótulo do intervalo resolvido — a tela diz a régua, não só o preset. */
export function rotuloIntervalo(preset, base = new Date()) {
  const { de, ate } = intervaloDoPeriodo(preset, base);
  return rotuloIntervaloLivre(de, ate);
}

/** Intervalo invertido (início depois do fim) não traz nada — avisar é
 *  melhor que devolver quadro vazio sem explicação. */
export const intervaloInvertido = (de, ate) => Boolean(de && ate && de > ate);

export const labelDoPeriodo = (preset) =>
  PERIODOS_ENTRADA.find((p) => p.id === preset)?.label
  || (preset === PERIODO_PERSONALIZADO ? "Período escolhido" : "");
